/**
 * DidYouKnowPopup — Rotating tip surface. Now throttled to once every 72h
 * and visually upgraded with a glass card, gradient ring and motion.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, X, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  areTipsDisabled,
  dismissForToday,
  disableTips,
  getTodaysTip,
  isDismissedToday,
  isInQuietHours,
  isThrottled,
} from "@/constants/dashboardTips";
import { acquirePopup, releasePopup, cancelPopup, POPUP_PRIORITIES } from "@/lib/popupQueue";

import { usePoints } from "@/hooks/usePoints";
import { useConnections } from "@/hooks/useConnections";
import { useWishlist } from "@/hooks/useWishlist";
import { useFamilyBonding } from "@/hooks/useFamilyBonding";

const SHOW_DELAY_MS = 2200;
const POPUP_ID = "did-you-know";

export function DidYouKnowPopup() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { streakDays } = usePoints();
  const { connections } = useConnections();
  const { items: wishlistItems } = useWishlist();
  const { completedCount: togetherCount } = useFamilyBonding();

  const [tip] = useState(() =>
    getTodaysTip({
      streakDays,
      hasReflectedToday: false,
      hasConnection: (connections?.length ?? 0) > 0,
      hasWishlist: (wishlistItems?.length ?? 0) > 0,
      togetherCount,
      hasCertificate: false,
    }),
  );

  useEffect(() => {
    if (areTipsDisabled() || isDismissedToday() || isInQuietHours() || isThrottled()) return;
    let cancelled = false;
    const t = setTimeout(() => {
      acquirePopup(POPUP_ID, POPUP_PRIORITIES.tip).then(() => {
        if (cancelled) {
          releasePopup(POPUP_ID);
          return;
        }
        setOpen(true);
      });
    }, SHOW_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
      cancelPopup(POPUP_ID);
    };
  }, []);

  const closeAndRelease = () => {
    setOpen(false);
    releasePopup(POPUP_ID);
  };

  const handleCta = () => {
    dismissForToday();
    const action = tip.action;
    if (action.type === "route") {
      navigate(action.value);
    } else if (action.type === "event") {
      window.dispatchEvent(new CustomEvent(action.value));
    } else if (action.type === "route+event") {
      navigate(action.route);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent(action.event));
      }, 250);
    }
    setTimeout(() => closeAndRelease(), 0);
  };

  const handleNotNow = () => {
    dismissForToday();
    closeAndRelease();
  };

  const handleNeverShow = () => {
    disableTips();
    closeAndRelease();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          dismissForToday();
          closeAndRelease();
        }
      }}
    >
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none">
        <VisuallyHidden>
          <DialogTitle>Did you know</DialogTitle>
          <DialogDescription>{tip.body}</DialogDescription>
        </VisuallyHidden>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.97 }}
              transition={{ type: "spring", damping: 22, stiffness: 280 }}
              className="gd-obsidian"
            >
              <button
                onClick={handleNotNow}
                aria-label="Close"
                className="absolute top-3 right-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-white/60 transition hover:bg-white/[0.12] hover:text-white active:scale-95"
                style={{ touchAction: "manipulation" }}
              >
                <X className="h-4 w-4" />
              </button>

              <div className="relative p-6 sm:p-7">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 shadow-[0_10px_24px_-10px_hsl(40_90%_50%/0.7),inset_0_1px_0_hsl(45_100%_90%/0.8)]">
                    <Lightbulb className="h-5 w-5 text-amber-950" />
                  </span>
                  <p className="gd-kicker text-amber-200/70">{tip.eyebrow}</p>
                </div>

                <p className="gd-display pr-6 text-lg leading-snug sm:text-xl">
                  {tip.body}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCta}
                    className="inline-flex h-11 items-center gap-1 rounded-full bg-gradient-to-b from-amber-300 to-amber-400 px-5 text-sm font-bold text-amber-950 shadow-[0_12px_28px_-12px_hsl(40_90%_50%/0.8),inset_0_1px_0_hsl(45_100%_92%/0.9)] transition hover:brightness-105 active:scale-[0.98]"
                    style={{ touchAction: "manipulation" }}
                  >
                    {tip.ctaLabel}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNotNow}
                    className="inline-flex h-11 items-center rounded-full border border-white/20 bg-white/[0.06] px-5 text-sm font-semibold text-white transition hover:bg-white/[0.12] active:scale-[0.98]"
                    style={{ touchAction: "manipulation" }}
                  >
                    Not now
                  </button>
                  <button
                    type="button"
                    onClick={handleNeverShow}
                    className="ml-auto text-[11px] text-white/50 underline-offset-2 transition hover:text-white hover:underline"
                    style={{ touchAction: "manipulation" }}
                  >
                    Don't show tips
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
